import React, { useEffect, useRef, useState } from 'react';
import './settings-modal.scss';
import { useDispatch, useSelector } from 'react-redux';
import {
  ConsoleLogger,
  DefaultDeviceController,
  DefaultVideoTransformDevice,
  isVideoTransformDevice,
  LogLevel,
} from 'amazon-chime-sdk-js';
import { useBackgroundBlur } from 'amazon-chime-sdk-component-library-react';
import { setAudioDevice, setMicDevice, setVideoDevice } from 'src/redux/actions/teleTherapyActions';


let isComponentInitialized = false;

const SelfVideoView = () => {
  const dispatch = useDispatch();
  const videoRef = useRef<HTMLVideoElement>(null);
  const { videoDevice, audioDevice, micDevice, blurBackground, isVideoEnabledFlag, isMicEnabled } = useSelector(
    ({ teleTherapy }: any) => teleTherapy,
  );
  const { createBackgroundBlurDevice } = useBackgroundBlur();
  let deviceController: any;
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const deviceControllerRef = useRef<DefaultDeviceController | null>(null);
  const [videoTransformDevice, setVideoTransformDevice] =
    useState<DefaultVideoTransformDevice | null>(null);

  useEffect(() => {
    init();

    return () => {
      deviceControllerRef.current?.destroy();
      deviceControllerRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (isComponentInitialized) {
      toggleVideoFeed(isVideoEnabledFlag);
    }
  }, [isVideoEnabledFlag]);

  useEffect(() => {
    if (isComponentInitialized) {
     toggleMicStatus(isMicEnabled);
    }
  }, [isMicEnabled]);

  useEffect(() => {
    if (isComponentInitialized && videoDevice) {
      handleVideoBackgroundState(blurBackground, videoDevice);
    }
  }, [videoDevice, blurBackground]);

  const init = async () => {
    deviceController = new DefaultDeviceController(
      new ConsoleLogger('DeviceController', LogLevel.OFF),
    );

    deviceControllerRef.current = deviceController;
    const audioDevices = await deviceController.listAudioInputDevices();
    const videoDevices = await deviceController.listVideoInputDevices();
    const audioOutputDevices = await deviceController.listAudioOutputDevices();
    
    setVideoDevices(videoDevices);
    toggleMicStatus(true);
    isComponentInitialized = true;

    if (!videoDevice) {
      dispatch(setVideoDevice(videoDevices[0]),);
    }

    if (!audioDevice) {
      dispatch(setAudioDevice(audioDevices[0]));
    }

    if (!micDevice) {
      dispatch(setMicDevice(audioOutputDevices[0]));
    }
  };

  const startVideoWithDevice = async (
    device: MediaDeviceInfo | DefaultVideoTransformDevice,
  ) => {
    if (!deviceControllerRef.current) return;

    try {
      if(videoRef.current !== null) {
        if (isVideoTransformDevice(device)) {
          await deviceControllerRef.current.startVideoInput(device);
          await deviceControllerRef.current.startVideoPreviewForVideoInput(
            videoRef.current,
          );
        } else {
          await deviceControllerRef.current.startVideoInput(device.deviceId);
          await deviceControllerRef.current.startVideoPreviewForVideoInput(
            videoRef.current,
          );
        }
      }
    } catch (error) {
      console.error('Error starting video with device:', error);
    }
  };

  const handleVideoBackgroundState = async (
    blurBackgroundState: 'light' | 'heavy' | false,
    device: MediaDeviceInfo,
  ) => {
    try {
      if (!device) return;

      if (!blurBackgroundState) {
        // handle disable blur state
        const intrinsicDevice =
          (await videoTransformDevice?.intrinsicDevice()) as MediaDeviceInfo;

        await startVideoWithDevice(intrinsicDevice || videoDevice);
        setVideoTransformDevice(null);
      } else {
        // handle blur state
        const backgroundBlurDevice = await createBackgroundBlurDevice(device);

        setVideoTransformDevice(backgroundBlurDevice);

        await startVideoWithDevice(
          backgroundBlurDevice as DefaultVideoTransformDevice,
        );
      }
    } catch (error) {
      console.error('Error enabling background blur:', error);
    }
  };

  const toggleVideoFeed = async (isVideoEnabledFlag: boolean) => {
    try {
      if (!deviceControllerRef.current) return;

      if (!isVideoEnabledFlag) {
        await deviceControllerRef.current.stopVideoInput();
      } else {
        if(videoRef?.current) {
          await deviceControllerRef.current.startVideoInput(
            videoTransformDevice || videoDevice || videoDevices[0],
          );
          await deviceControllerRef.current.startVideoPreviewForVideoInput(
            videoRef.current,
          );
        }
      }
    } catch (error) {
      console.error('Error toggling video feed:', error);
    }
  };

  const toggleMicStatus = async (isMicEnabled: boolean) => {
    try {
      if (!deviceControllerRef.current) return;

      if (!isMicEnabled) {
        await deviceControllerRef.current.stopAudioInput();
      } else {
        await deviceControllerRef.current.startAudioInput(audioDevice);
      }
    } catch (error) {
      console.error('Error toggling mic status:', error);
    }
  };

  return (
    <>
      <video ref={videoRef} autoPlay={true} disablePictureInPicture={true} />
    </>
  );
};

export default SelfVideoView;
